import { appConfig } from './app.js';
import { estateAdminDomainRoot, estateDomainRoot, getEstate } from './aws.js';
import { regions } from './regions.js';
export function getRuntimeEnvironment(name) {
    if (name === 'local' || name === 'dev' || name === 'uat' || name === 'prod') {
        return name;
    }
    throw new Error(`Unknown environment "${name}". Expected one of: local, dev, uat, prod`);
}
const productionPublicRegions = ['au'];
/** Regions available to the customer-facing planner in each environment. */
export function publicRegionsFor(environment) {
    switch (environment) {
        case 'local':
        case 'dev':
        case 'uat':
            return regions.map((region) => region.code);
        case 'prod':
            return regions
                .filter((region) => productionPublicRegions.includes(region.code))
                .map((region) => region.code);
        default:
            throw new Error(`Unknown environment "${environment}"`);
    }
}
export const environments = {
    dev: {
        name: 'dev',
        erpSyncSchedule: { minute: '0', hour: '18' }, // 4am AEST
        logRetentionDays: 30,
        alarms: false,
        sessionRecording: false,
        extraCorsOrigins: ['http://localhost:5173', 'http://localhost:5174'],
    },
    uat: {
        name: 'uat',
        erpSyncSchedule: { minute: '15', hour: '18' },
        logRetentionDays: 90,
        alarms: true,
        sessionRecording: false,
        extraCorsOrigins: [],
    },
    prod: {
        name: 'prod',
        erpSyncSchedule: { minute: '30', hour: '18' },
        logRetentionDays: 365,
        alarms: true,
        sessionRecording: true,
        extraCorsOrigins: [],
    },
};
export function getEnvironment(name) {
    const env = environments[name];
    if (!env) {
        throw new Error(`Unknown environment "${name}". Expected one of: dev, uat, prod`);
    }
    return env;
}
/**
 * Environment prefix for hostnames. Skipped in prod, and skipped when the
 * estate's domain root already starts with the environment name (PAG's dev
 * root is dev.prostarmanage.com — no point in planner.au.dev.dev.…).
 */
function envPrefix(env, domainRoot) {
    return env === 'prod' || domainRoot.startsWith(`${env}.`) ? '' : `${env}.`;
}
/**
 * Region label for hostnames. Skipped when the estate's domain root for this
 * environment is already region-specific (PAG/Lugna prod use per-country
 * brand domains, so slug.protectoraluminium.com.au — not slug.au.…com.au).
 */
function regionLabel(env, region) {
    const root = getEstate(appConfig.estate).domainRoot[env];
    return typeof root === 'string' ? `${region.code}.` : '';
}
/**
 * Public site domain for an environment + region, derived from the estate's
 * domain root, e.g. planner-template.au.dev.<domainRoot> in dev or
 * planner-template.<brand prod domain> in prod.
 */
export function siteDomain(env, region, domainRoot) {
    return `${appConfig.appSlug}.${regionLabel(env, region)}${envPrefix(env, domainRoot)}${domainRoot}`;
}
/**
 * Admin portal domain for an environment (single portal across regions).
 * Pass the estate's admin root (`estateAdminDomainRoot`), which may differ
 * from the public site roots — PAG keeps admin on apps.prostarmanage.com in
 * every environment while prod sites use the brand domains.
 */
export function adminDomain(env, domainRoot) {
    return `admin.${appConfig.appSlug}.${envPrefix(env, domainRoot)}${domainRoot}`;
}
/** API domain for an environment (single API across regions). */
export function apiDomain(env, domainRoot) {
    return `api.${appConfig.appSlug}.${envPrefix(env, domainRoot)}${domainRoot}`;
}
/** All CORS origins the API should allow for an environment. */
export function corsAllowedOrigins(env, allRegions) {
    const settings = getEnvironment(env);
    const estate = getEstate(appConfig.estate);
    return [
        // Each region's origin derives from its own domain root — prod roots can
        // differ per country (.com.au / .co.nz).
        ...allRegions.map((region) => `https://${siteDomain(env, region, estateDomainRoot(estate, env, region.code))}`),
        `https://${adminDomain(env, estateAdminDomainRoot(estate, env))}`,
        ...settings.extraCorsOrigins,
    ];
}
//# sourceMappingURL=environments.js.map